import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { meQueryOptions, useMe } from '~/api/auth';
import { hourLabel, meHourlyQueryOptions } from '~/api/hourly';
import { HourlyForm } from '~/features/hourly/HourlyForm';
import { desktop } from '~/lib/desktop';
import { Button } from '~/ui/Button';
import { Hint } from '~/ui/Hint';

/**
 * The desktop app's hourly prompt: a window on top of other apps that asks what
 * the person did in the hour that just ended (`?start=` is the API's promptFor).
 * Send and Skip close it; Later brings it back after the company's snooze. An
 * hour answered somewhere else closes it too. Outside the app shell; signed out
 * it shows nothing.
 */
export const Route = createFileRoute('/check-in')({
  validateSearch: (s: Record<string, unknown>): { start?: string } => ({
    start: typeof s['start'] === 'string' ? s['start'] : undefined,
  }),
  beforeLoad: async ({ context }) => {
    await context.queryClient.ensureQueryData(meQueryOptions).catch(() => undefined);
  },
  component: CheckInPage,
});

function CheckInPage() {
  const me = useQuery(meQueryOptions);
  const { start } = Route.useSearch();
  useEffect(() => {
    document.title = '11Deeds check-in';
  }, []);
  if (!me.data) return null;
  return <CheckIn start={start} />;
}

const noDrag = '[-webkit-app-region:no-drag]';

function CheckIn({ start }: { start?: string }) {
  const bridge = desktop();
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const hourly = useQuery(meHourlyQueryOptions);
  const at = start ? Date.parse(start) : NaN;
  const slot = hourly.data?.slots.find((s) => Date.parse(s.start) === at) ?? null;
  const gone = hourly.data !== undefined && (slot === null || slot.status !== 'due');

  // Answered or skipped elsewhere (the browser, another device): nothing left to ask.
  useEffect(() => {
    if (gone) bridge?.closePrompt?.('done');
  }, [bridge, gone]);

  const later = () => bridge?.closePrompt?.('later');

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-paper text-ink">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-line bg-accent-deep px-4 text-sm font-medium text-white [-webkit-app-region:drag]">
        <span className="flex-1">Hourly check-in</span>
        <Hint label="Later" side="bottom">
          <button
            type="button"
            onClick={later}
            aria-label="Later"
            className={`${noDrag} grid size-7 place-items-center rounded opacity-80 hover:bg-white/10 hover:opacity-100`}
          >
            <X size={15} aria-hidden />
          </button>
        </Hint>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {slot && slot.status === 'due' ? (
          <>
            <h1 className="mb-3 text-lg font-semibold">
              What did you do from {hourLabel(slot, tz)}?
            </h1>
            <HourlyForm
              slot={slot}
              onDone={() => bridge?.closePrompt?.('done')}
              extra={
                <Button type="button" variant="secondary" onClick={later}>
                  Later
                </Button>
              }
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
